import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { NeedRow, Panel, Quiet, ScrollList, Stat } from "@/components/home/panel";
import { RowIcon, TONE_RANK, type IconName, type Tone } from "@/components/home/tokens";
import { RankPanel } from "@/components/home/RankPanel";
import { TodayCard } from "@/pages/home/TodayCard";
import type { NeedCounts, ProducerData } from "@/pages/home/types";
import type { FreshAlert, FreshAlertListResponse } from "@/types";

interface FreshCounts {
  last_24h: number;
  last_1h: number;
}

interface Need {
  id: string;
  icon: IconName;
  tone: Tone;
  count: number;
  label: string;
  hint: string;
  to: string;
}

/** Build the attention rows.
 *
 *  Counts are **windowed** — the lifetime backlog runs into the thousands and
 *  never moves, so it read as a permanent red badge rather than a task; the
 *  backlog now trails as muted context. The list is **sorted by severity** —
 *  the old one pushed tiles in source order, putting the rose "publishes
 *  failed" tile last, under two ambers.
 */
export function buildNeeds(n: NeedCounts, allowed: Set<string>): Need[] {
  const out: Need[] = [];
  const older = (backlog: number, shown: number) =>
    backlog > shown ? ` · ${(backlog - shown).toLocaleString()} older` : "";

  if (n.failed_jobs_24h) {
    out.push({
      id: "failed",
      icon: "x",
      tone: "rose",
      count: n.failed_jobs_24h,
      label: n.failed_jobs_24h === 1 ? "publish failed" : "publishes failed",
      hint: "In the last 7 days",
      to: allowed.has("/admin/jobs") ? "/admin/jobs" : "/publish",
    });
  }
  if (n.stalled_deals && allowed.has("/deals")) {
    out.push({
      id: "stalled",
      icon: "clock",
      tone: "amber",
      count: n.stalled_deals,
      label: n.stalled_deals === 1 ? "deal going cold" : "deals going cold",
      hint: "Untouched for 7+ days",
      to: "/deals",
    });
  }
  if (n.missing_phone && allowed.has("/placements")) {
    out.push({
      id: "phone",
      icon: "phone",
      tone: "amber",
      count: n.missing_phone,
      label: "missing a phone",
      hint: `This week${older(n.missing_phone_backlog, n.missing_phone)}`,
      to: "/placements?missing=phone",
    });
  }
  if (n.missing_offer && allowed.has("/placements")) {
    out.push({
      id: "offer",
      icon: "percent",
      tone: "amber",
      count: n.missing_offer,
      label: "without a commission",
      hint: `This week${older(n.missing_offer_backlog, n.missing_offer)}`,
      to: "/placements?missing=offer",
    });
  }
  return out.sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]);
}

/** The needs list + setup steps, as a panel. Reused by ManagerHome. */
export function NeedsPanel({
  needs,
  steps,
  title = "Needs you",
}: {
  needs: Need[];
  steps: { id: string; label: string; cta_label: string | null; cta_href: string | null; cta_external?: boolean }[];
  title?: string;
}) {
  const empty = needs.length === 0 && steps.length === 0;
  return (
    <Panel title={title} right={needs.length ? `${needs.length}` : undefined} flush>
      {empty ? (
        <Quiet
          title="You're all caught up"
          hint="Nothing needs fixing — go publish something."
        />
      ) : (
        <ScrollList deps={needs.length + steps.length}>
          {needs.map(({ id, ...t }) => (
            <NeedRow key={id} {...t} />
          ))}
          {steps.map((s) => (
            <Link
              key={s.id}
              to={s.cta_href!}
              className="group flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-white/[0.02] transition"
            >
              <span className="flex items-center justify-center h-7 w-7 rounded-lg flex-shrink-0 bg-accent-500/10 text-accent-300">
                <RowIcon name="circle" size={15} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] text-slate-200 truncate">{s.label}</span>
                <span className="block text-[10.5px] text-accent-300 mt-0.5">
                  {s.cta_label ?? "Open"}
                </span>
              </span>
            </Link>
          ))}
        </ScrollList>
      )}
    </Panel>
  );
}

export function ProducerHome({
  data,
  allowed,
}: {
  data: ProducerData;
  allowed: Set<string>;
}) {
  // Second wave: the market rails hit the scraper DBs on another host, so they
  // stay off the critical path of the landing response.
  const [fresh, setFresh] = useState<FreshCounts | null>(null);
  const [alerts, setAlerts] = useState<FreshAlert[]>([]);

  useEffect(() => {
    if (!allowed.has("/fresh")) return;
    let alive = true;
    api<FreshCounts>("/api/fresh/counts?seller=owner&source=both")
      .then((r) => alive && setFresh(r))
      .catch(() => {});
    api<FreshAlertListResponse>("/api/fresh/alerts")
      .then((r) => alive && setAlerts(r.items))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [allowed]);

  const b = data.me;
  const needs = buildNeeds(data.needs, allowed);
  const steps = (data.onboarding?.steps ?? []).filter((s) => !s.done && s.cta_href);
  const firedAlerts = alerts.filter((a) => a.enabled && a.last_match_count > 0).slice(0, 4);

  return (
    <div className="space-y-3">
      {/* KPI row — the ring is sized to the tile height so the row aligns. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TodayCard b={b} />
        <Stat
          label="This month"
          value={b.placements_this_month.toLocaleString()}
          foot={
            b.placement_goal_daily > 0
              ? `${b.placement_days_hit} of ${b.placement_days_elapsed} days on target`
              : undefined
          }
          bar={
            b.placement_days_elapsed > 0
              ? b.placement_days_hit / b.placement_days_elapsed
              : undefined
          }
        />
        <Stat
          label="Deals closed"
          value={b.deals_done}
          suffix={b.deal_goal_monthly > 0 ? `/ ${b.deal_goal_monthly}` : undefined}
          foot={b.deals_lost > 0 ? `${b.deals_lost} lost this month` : "this month"}
          bar={b.deal_goal_monthly > 0 ? b.deals_done / b.deal_goal_monthly : undefined}
          tone="emerald"
          to={allowed.has("/deals") ? "/deals" : undefined}
        />
      </div>

      {/* Work row — needs on the left, market digest on the right. */}
      <div className="grid gap-3 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2">
          <NeedsPanel needs={needs} steps={steps} />
        </div>
        <div className="space-y-3 min-w-0">
          <RankPanel />
          {allowed.has("/fresh") && (
          <Panel title="New since yesterday" flush>
            {!fresh?.last_24h && firedAlerts.length === 0 ? (
              <Quiet title="Nothing new" hint="No fresh owner listings today." />
            ) : (
              <div className="divide-y divide-white/[0.04]">
                {fresh?.last_24h ? (
                  <NeedRow
                    icon="sprout"
                    tone="emerald"
                    count={fresh.last_24h}
                    label="fresh owner listings"
                    hint={
                      fresh.last_1h ? `${fresh.last_1h} in the last hour` : "last 24 hours"
                    }
                    to="/fresh"
                  />
                ) : null}
                {firedAlerts.map((a) => (
                  <NeedRow
                    key={a.id}
                    icon="bell"
                    tone="accent"
                    count={a.last_match_count}
                    label={a.name}
                    hint="matches on its last run"
                    to="/fresh"
                  />
                ))}
              </div>
            )}
          </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
